package one.healthium.meal_plans.tutorial.screen

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import loco.TUTORIAL_PERSONAL_MEAL_PLAN_DESCRIPTION
import loco.TUTORIAL_PERSONAL_MEAL_PLAN_NEGATIVE_BUTTON
import loco.TUTORIAL_PERSONAL_MEAL_PLAN_POSITIVE_BUTTON
import loco.TUTORIAL_PERSONAL_MEAL_PLAN_TITLE
import one.healthium.ui.common.event_producer.EventEffect
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppProgressButton
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.components.AppTextButton
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

interface PersonalMealPlanTutorialEventDelegate {
    fun onMealPlanClicked(id: String)
    fun onCloseClicked()
}

@Composable
fun PersonalMealPlanTutorialScreen(eventDelegate: PersonalMealPlanTutorialEventDelegate) {
    val viewModel = koinViewModel<PersonalMealPlanTutorialViewModel>()
    val state by viewModel.state.collectAsState()

    EventEffect(viewModel.events) { event ->
        when (event) {
            is PersonalMealPlanTutorialEvent.OpenMealPlan -> eventDelegate.onMealPlanClicked(event.id)
        }
    }

    PersonalMealPlanTutorialScreenContent(
        state = state,
        onNextClicked = {
            viewModel.onActivatePersonalMealPlan()
            viewModel.onMarkTutorialShown()
        },
        onSkipClicked = {
            eventDelegate.onCloseClicked()
            viewModel.onMarkTutorialShown()
        },
    )

    AlertUiMessageHandler(viewModel.uiMessages)
}

@Composable
private fun PersonalMealPlanTutorialScreenContent(
    state: PersonalMealPlanTutorialUiState,
    onNextClicked: () -> Unit,
    onSkipClicked: () -> Unit,
) {
    Column(
        modifier = Modifier
            .navigationBarsPadding()
            .padding(horizontal = 32.dp)
            .heightIn(max = 640.dp)
            .fillMaxWidth()
            .verticalScroll(rememberScrollState()),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Spacer(Modifier.height(36.dp))
        AppText(
            text = stringResource(loco.Res.string.TUTORIAL_PERSONAL_MEAL_PLAN_TITLE),
            style = AppTheme.tutorialStyle?.titleStyle ?: AppTheme.textStyles.secondary_30_500,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(16.dp))
        AppText(
            text = stringResource(loco.Res.string.TUTORIAL_PERSONAL_MEAL_PLAN_DESCRIPTION),
            style = AppTheme.tutorialStyle?.messageStyle ?: AppTheme.textStyles.secondary_18_400,
            color = AppTheme.colors.text500,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(48.dp))
        AppProgressButton(
            modifier = Modifier.defaultMinSize(minWidth = 176.dp),
            onClick = onNextClicked,
            loading = state.loading,
            text = stringResource(loco.Res.string.TUTORIAL_PERSONAL_MEAL_PLAN_POSITIVE_BUTTON),
        )
        Spacer(Modifier.height(12.dp))
        AppTextButton(
            onClick = onSkipClicked,
        ) {
            Text(stringResource(loco.Res.string.TUTORIAL_PERSONAL_MEAL_PLAN_NEGATIVE_BUTTON))
        }
        Spacer(Modifier.height(50.dp))
    }
}

@Composable
@Preview(showBackground = true)
fun PersonalMealPlanTutorialScreenPreview() {
    AppTheme {
        PersonalMealPlanTutorialScreenContent(
            state = PersonalMealPlanTutorialUiState(),
            onNextClicked = {},
            onSkipClicked = {},
        )
    }
}
